import React from 'react';
import { ForecastChart, RunwayChart } from './charts';
import { money } from './format';
import { Icon } from './icons';
import { HelpTip } from './Tooltip';
import { ScenarioPanel } from './scenarios';

export function CashFlowView({
  balance,
  runway,
  forecast,
  monthlyRecurringNet,
  recurring,
  forecastOverlays = [],
  forecastBands = null,
  forecastConfidenceLow = false,
  scenarios = [],
  onSaveScenario,
  onDeleteScenario,
  onToggleScenario,
  onGoToRecurring,
}) {
  // Only explain the notation the user can actually see on this chart.
  const runwayEvents = (runway?.days || []).flatMap((d) => d.events || []);
  const inFlightCount = runwayEvents.filter((e) => e.inFlight).length;

  if (recurring.length === 0) {
    return (
      <div className="pg">
        <div className="pg-section">
          <p className="pg-note">No forecast yet — the projection is driven by your recurring items, so it needs at least one.</p>
          {onGoToRecurring && (
            <div>
              <button type="button" onClick={onGoToRecurring} className="btn-primary pressable">
                <Icon name="repeat" size={15} sw={2.2} />
                Go to Recurring
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Month-by-month detail beneath the chart: baseline balance, net change from
  // the prior month, and each enabled scenario's projected balance side by side.
  const monthRows = forecast.map((m, i) => {
    const prevBalance = i === 0 ? balance : forecast[i - 1].balance;
    return {
      label: m.label,
      balance: m.balance,
      net: m.balance - prevBalance,
      scenarios: forecastOverlays.map((o) => o.data?.[i]?.balance),
    };
  });
  const lowestRow = monthRows.reduce((min, r) => (!min || r.balance < min.balance ? r : min), null);
  const gridCols = `1fr 88px 100px${forecastOverlays.map(() => ' 100px').join('')}`;
  const tableMinWidth = 300 + forecastOverlays.length * 100;

  return (
    <div className="pg">
      {runway?.days?.length > 0 && (
        <div className="pg-section rise">
          <div className="eyebrow">Cash runway · next {runway.days.length - 1} days</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span
              className="num"
              style={{
                fontSize: 30, fontWeight: 600, letterSpacing: '-0.03em',
                color: runway.status === 'shortfall' ? 'var(--alarm)' : 'var(--ink)',
              }}
            >
              {money(runway.minBalance.balance, { cents: false })}
            </span>
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              low point · {runway.minBalance.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
          <RunwayChart
            days={runway.days}
            floor={runway.floor}
            breachKey={runway.breach?.key ?? null}
            height={200}
          />
          {inFlightCount > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>
                {inFlightCount === 1 ? 'A payment is' : `${inFlightCount} payments are`} settling — posted on the card, not yet out of your bank.
              </span>
              <HelpTip
                width={280}
                text="One side of the transfer has already posted, so the money is committed even though your bank balance doesn't show it yet. It's included in the projection above."
              />
            </div>
          )}
          {runway.lowConfidence && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontStyle: 'italic' }}>Spending range not shown — not enough history yet.</span>
              <HelpTip width={260} text="The shaded band is a pessimistic case based on how much your variable spending swings month to month. It needs at least 3 completed months." />
            </div>
          )}
        </div>
      )}

      <div className="pg-section rise">
        <div className="eyebrow">Projected balance · 12 months</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span className="num" style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.03em', color: 'var(--ink)' }}>
            {money(forecast[forecast.length - 1]?.balance ?? balance, { cents: false })}
          </span>
          <span className="num" style={{ fontSize: 13, fontWeight: 600, color: monthlyRecurringNet < 0 ? 'var(--alarm)' : 'var(--ink-2)' }}>
            {money(monthlyRecurringNet, { sign: true, cents: false })}/mo
          </span>
        </div>
        <ForecastChart data={forecast} overlays={forecastOverlays} bands={forecastBands} height={220} />
        {forecastConfidenceLow && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontStyle: 'italic' }}>Forecast confidence: limited — not enough history yet.</span>
            <HelpTip width={260} text="Confidence bands show a likely range based on your spending variability." />
          </div>
        )}
        {!forecastConfidenceLow && forecastBands && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
            Shaded range shows likely outcomes based on past variance.
          </div>
        )}
        {/* Doubles as the chart legend: each enabled row carries its line's
            dashed swatch, so the old name-only legend would just repeat it. */}
        <ScenarioPanel
          scenarios={scenarios}
          recurring={recurring}
          overlays={forecastOverlays}
          baselineEnd={forecast[forecast.length - 1]?.balance ?? balance}
          onSave={onSaveScenario}
          onDelete={onDeleteScenario}
          onToggle={onToggleScenario}
        />

        <div style={{ borderTop: '1px solid var(--line)', margin: '20px 0 14px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div className="eyebrow">Month by month</div>
          {lowestRow && lowestRow.balance < 500 && (
            <span style={{ fontSize: 12, color: lowestRow.balance < 0 ? 'var(--neg)' : 'var(--warn)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon name="alert" size={12} />
              Lowest point: {lowestRow.label} at {money(lowestRow.balance, { cents: false })}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', overflowX: 'auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              gap: 8,
              padding: '6px 4px',
              fontSize: 11,
              color: 'var(--ink-3)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              minWidth: tableMinWidth,
            }}
          >
            <span>Month</span>
            <span style={{ textAlign: 'right' }}>Net</span>
            <span style={{ textAlign: 'right' }}>Baseline</span>
            {forecastOverlays.map((o) => (
              <span key={o.id} style={{ textAlign: 'right', color: o.color || 'var(--ink-3)' }}>{o.name}</span>
            ))}
          </div>
          <div className="scroll" style={{ maxHeight: 320, overflowY: 'auto', minWidth: tableMinWidth }}>
            {monthRows.map((r, i) => {
              const isNeg = r.balance < 0;
              const isLow = !isNeg && r.balance < 500;
              return (
                <div
                  key={i}
                  style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 8, padding: '11px 4px', fontSize: 13, borderBottom: '1px solid var(--line-soft)' }}
                >
                  <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{r.label}</div>
                  <div className="num" style={{ textAlign: 'right', color: r.net < 0 ? 'var(--alarm)' : 'var(--ink-2)', fontWeight: 500 }}>
                    {money(r.net, { sign: true, cents: false })}
                  </div>
                  <div
                    className="num"
                    style={{ textAlign: 'right', fontWeight: 600, color: isNeg ? 'var(--alarm)' : isLow ? 'var(--warn)' : 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}
                  >
                    {isNeg && <Icon name="alert" size={12} />}
                    {money(r.balance, { cents: false })}
                  </div>
                  {r.scenarios.map((v, si) => (
                    <div key={si} className="num" style={{ textAlign: 'right', color: 'var(--ink-2)', fontWeight: 600 }}>
                      {Number.isFinite(v) ? money(v, { cents: false }) : '—'}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <p className="pg-note">
        Driven by <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{recurring.length} recurring items</b> totalling{' '}
        <b className="num" style={{ color: monthlyRecurringNet < 0 ? 'var(--alarm)' : 'var(--ink)', fontWeight: 600 }}>{money(monthlyRecurringNet, { sign: true, cents: false })}</b> net each month.
      </p>
    </div>
  );
}
